<template>
    <div
        id="app"
        class="h-100"
        :class="[skinClasses]"
    >
        <!-- 통합 프로그레스바 (자동 + 수동) -->
        <b-progress
            v-if="loadingStatus || manualLoadingStatus"
            class="progress-bar bg-primary progress-linear-indeterminate"
            style="position:fixed;z-index:2000;top:0;"
            variant="primary"
        ></b-progress>
        <div
            v-if="isTourActive"
            class="position-fixed"
            :style="{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                zIndex: 2000
            }">
        </div>
        <component :is="layout">
            <!-- dashboard-header -->
            <template
                v-if="fetchIfDashboardPage"
                v-slot:dashboard-header-left
            >
                <dashboard-header-left
                    :isEditMode="isEditMode"
                    @setCustomDashboard="setCustomDashboard"
                ></dashboard-header-left>
            </template>
            <template
                v-if="fetchIfDashboardPage && isEditMode"
                v-slot:dashboard-header-right
            >
                <dashboard-header-right
                    isResetMode
                    @setCustomDashboard="setCustomDashboard"
                    @resetCustomDashboard="resetCustomDashboard"
                    @saveCustomDashboard="saveCustomDashboard"
                ></dashboard-header-right>
            </template>
            <!-- common select -->
            <template v-slot:common-select>
                <CommonSelect v-if="!isEditMode"/>
            </template>
            <router-view />
        </component>
        <scroll-to-top v-if="isScrollToTop" />
        <SwUpdated/>
    </div>
</template>

<script>
import SwUpdated from '@/views/Shared/SwUpdated.vue'
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue'
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch, provide, reactive, computed, getCurrentInstance, ref } from '@vue/composition-api'
import { useWindowSize, useCssVar, useWindowScroll } from '@vueuse/core'
import useAppConfig from '@core/app-config/useAppConfig'
import useToasts from '@/composables/useToasts'
import useTour from '@/composables/useTour'
import useDashboardHeader from '@/composables/useDashboardHeader'
import bus from '@/libs/bus'
import Ripple from 'vue-ripple-directive'
import CommonSelect from '@/components/commonSelect/Index.vue'
import DashboardHeaderLeft from '@/components/DashboardHeader/DashboardHeaderLeft.vue'
import DashboardHeaderRight from '@/components/DashboardHeader/DashboardHeaderRight.vue'
const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
    directives: {
        Ripple
    },
    components: {
        CommonSelect,
        DashboardHeaderLeft,
        DashboardHeaderRight,
        LayoutHorizontal,
        LayoutVertical,
        LayoutFull,
        ScrollToTop,
        SwUpdated
    },
    computed: {
        layout () {
            if (this.$route.meta.layout === 'full') return 'layout-full'
            return `layout-${this.contentLayoutType}`
        },
        contentLayoutType () {
            return this.$store.state.appConfig.layout.type
        },
        isScrollToTop () {
            const { y } = useWindowScroll()
            return y.value > 250
        },
        fetchIfDashboardPage () {
            return this.$route.meta.headerType === 'dashboard'
        }
    },
    beforeCreate () {
        const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

        for (let i = 0, len = colors.length; i < len; i++) {
            $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
        }

        // Set Theme Breakpoints
        const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

        for (let i = 0, len = breakpoints.length; i < len; i++) {
            $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
        }

        // Set RTL
        const { isRTL } = $themeConfig.layout
        document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
    },
    beforeDestroy () {
        bus.$off('start:progress', this.startProgress)
        bus.$off('end:progress', this.endProgress)
        bus.$off('start:manual-progress', this.startManualProgress)
        bus.$off('end:manual-progress', this.endManualProgress)
        bus.$off('tour-start:dashboard', this.startTourDashboard)
        bus.$off('tour-start:dashboardEdit', this.startTourDashboardEdit)
    },
    setup () {
        const vm = getCurrentInstance().proxy
        const store = vm.$store
        const { skin, skinClasses } = useAppConfig()

        // If skin is dark when initialized => Add class to body
        if (skin.value === 'dark') document.body.classList.add('dark-layout')

        // Provide toast for Composition API usage
        // This for those apps/components which uses composition API
        // Demos will still use Options API for ease
        provideToast({
            hideProgressBar: true,
            closeOnClick: false,
            closeButton: false,
            icon: false,
            timeout: 3000,
            transition: 'Vue-Toastification__fade'
        })

        // theme
        const appThemeColors = reactive($themeColors)
        const theme = computed(() => store.getters['appConfig/theme'])
        const themeColors = ref({
            ...appThemeColors,
            currentColor: 'currentColor',
            range: [
                appThemeColors.secondary,
                appThemeColors.danger,
                appThemeColors.warning,
                appThemeColors.success,
                appThemeColors.info,
                appThemeColors.primary
            ]
        })
        provide('themeColors', themeColors)
        provide('theme', theme)

        // Set Window Width in store
        store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
        const { width: windowWidth } = useWindowSize()
        watch(windowWidth, val => {
            store.commit('app/UPDATE_WINDOW_WIDTH', val)
        })

        // toast
        useToasts()

        // bar progress loading (자동 - API 요청용)
        let loadingStatus = ref(false)
        const startProgress = () => (loadingStatus.value = true)
        const endProgress = () => (loadingStatus.value = false)
        bus.$on('start:progress', startProgress)
        bus.$on('end:progress', endProgress)

        // manual progress loading (수동 - 비동기 작업용)
        let manualLoadingStatus = ref(false)
        const startManualProgress = () => (manualLoadingStatus.value = true)
        const endManualProgress = () => (manualLoadingStatus.value = false)
        bus.$on('start:manual-progress', startManualProgress)
        bus.$on('end:manual-progress', endManualProgress)

        /* custom dashboard */
        const {
            isResetMode,
            isEditMode,
            updateEditMode,
            updateResetMode,
            saveDashboard,
            cancelDashboard
        } = useDashboardHeader()

        const saveCustomDashboard = () => saveDashboard()
        const resetCustomDashboard = () => updateResetMode(true)
        const setCustomDashboard = (isEditMode) => isEditMode ? updateEditMode(isEditMode) : cancelDashboard(isEditMode)

        // dashboard tour
        const { startTourDashboard, startTourDashboardEdit, isTourActive } = useTour()
        bus.$on('tour-start:dashboard', startTourDashboard)
        bus.$on('tour-start:dashboardEdit', startTourDashboardEdit)
        return {
            isTourActive,
            skinClasses,
            loadingStatus,
            manualLoadingStatus,
            startProgress,
            endProgress,
            startManualProgress,
            endManualProgress,
            isResetMode,
            isEditMode,
            resetCustomDashboard,
            saveCustomDashboard,
            cancelDashboard,
            setCustomDashboard,
            startTourDashboard,
            startTourDashboardEdit
        }
    }
}
</script>
